import './PlanImportModal.scss';
import { observer }              from 'mobx-react-lite';
import View                      from '@widesk-ui/components/View';
import Alert                     from '@widesk-ui/components/Alert';
import Button                    from '@widesk-ui/components/Button';
import { Modal }                 from '@widesk-ui/components/modal/Modal';
import useDesignTokens           from '@/hooks/useDesignTokens';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import planImportStore           from '@/TokamapEditor/planImport/planImportStore';
import type { PlanImportStep }   from '@/TokamapEditor/planImport/planImportStore';
import PlanFileStep              from '@/TokamapEditor/planImport/PlanFileStep';
import PlanRegionStep            from '@/TokamapEditor/planImport/PlanRegionStep';
import PlanReviewStep            from '@/TokamapEditor/planImport/PlanReviewStep';

const STEPS: { step: PlanImportStep; label: string }[] = [
	{ step: 'file', label: 'Le plan' },
	{ step: 'region', label: 'Zone d’analyse' },
	{ step: 'review', label: 'Vérification' },
	{ step: 'placement', label: 'Placement' },
];

const StepBar = observer(() => {
	const tokens = useDesignTokens();
	const currentIndex = STEPS.findIndex(entry => entry.step === planImportStore.step);

	return (
		<View row gap={6} wrap>
			{STEPS.map(({ step, label }, index) => {
				const current = index === currentIndex;
				const done = index < currentIndex;

				return (
					<View
						key={step}
						row
						gap={8}
						style={{
							alignItems: 'center',
							padding: '4px 10px 4px 4px',
							borderRadius: 999,
							background: current ? `color-mix(in srgb, ${tokens.color.primary} 12%, transparent)` : 'transparent',
						}}
					>
						<View
							center
							size={12}
							style={{
								width: 24,
								height: 24,
								borderRadius: '50%',
								fontWeight: 600,
								background: current || done ? tokens.color.primary : tokens.fill.secondary,
								color: current || done ? tokens.surface.card : tokens.text.secondary,
							}}
						>
							{done ? '✓' : index + 1}
						</View>
						<View
							size={13}
							color={current ? tokens.color.primary : tokens.text.secondary}
							style={{ fontWeight: current ? 600 : 400 }}
						>
							{label}
						</View>
					</View>
				);
			})}
		</View>
	);
});

const Footer = observer(() => {
	const tokens = useDesignTokens();
	const { step, detecting, busy, region, readyCount } = planImportStore;

	if ('region' === step) {
		return (
			<View row gap={12} style={{ alignItems: 'center', justifyContent: 'flex-end' }}>
				{!region && <View size={13} color={tokens.text.secondary}>Sans zone, tout le plan est analysé</View>}
				<Button type="primary" loading={detecting} onClick={() => void planImportStore.detect()}>
					Lancer la détection
				</Button>
			</View>
		);
	}

	if ('review' === step) {
		const toPlacement = async () => {
			if (await planImportStore.toPlacement()) leftSidebarStore.openPlanPlacement();
		};

		return (
			<View row gap={12} wrap style={{ alignItems: 'center' }}>
				<Button onClick={() => planImportStore.backToRegion()}>← Modifier la zone</Button>
				<View flex />
				{planImportStore.error && <View size={13} color={tokens.color.error}>{planImportStore.error}</View>}
				<View size={13} color={tokens.text.secondary}>
					{readyCount ? `${planImportStore.importedCount} forme(s) seront importées` : 'Choisissez le type de POI à droite pour continuer'}
				</View>
				<Button type="primary" disabled={!readyCount} loading={busy} onClick={toPlacement}>
					Placer sur la carte →
				</Button>
			</View>
		);
	}

	return null;
});

const PlanImportModal = observer(() => {
	const { step } = planImportStore;

	if (!planImportStore.modalOpened) return null;

	return (
		<Modal
			open
			fullScreen
			bodyPadding={0}
			onClose={() => planImportStore.close()}
			title={(
				<View row gap={24} wrap style={{ alignItems: 'center' }}>
					<View>Importer un plan</View>
					<StepBar />
					{planImportStore.file && <View size={13} style={{ fontWeight: 400 }}>{planImportStore.file.name}</View>}
				</View>
			)}
			footer={'file' === step ? null : <Footer />}
		>
			<View className="plan-import-modal-body" style={{ height: 'file' === step ? 'calc(100vh - 146px)' : 'calc(100vh - 218px)', minHeight: 420, display: 'flex', flexDirection: 'column' }}>
				{'file' === step && planImportStore.error && (
					<View p="md"><Alert type="error" showIcon message={planImportStore.error} /></View>
				)}
				{'file' === step && <PlanFileStep />}
				{'region' === step && <PlanRegionStep />}
				{'review' === step && <PlanReviewStep />}
			</View>
		</Modal>
	);
});

export default PlanImportModal;
